/**
 * CustomerMetricsSummary Component
 * Displays customer engagement metrics and statistics
 */

import {
  TrendingUp,
  Calendar,
  Car,
  Mail,
  Euro,
  Wrench,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import type { CustomerMetrics } from '../../hooks/useCustomers';

interface CustomerMetricsSummaryProps {
  metrics: CustomerMetrics;
  isLoading?: boolean;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Nunca';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function CustomerMetricsSummary({ metrics, isLoading }: CustomerMetricsSummaryProps) {
  if (isLoading) {
    return <CustomerMetricsSummarySkeleton />;
  }

  const metricCards = [
    {
      icon: TrendingUp,
      label: 'Total visitas',
      value: metrics.totalVisits.toString(),
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      icon: Euro,
      label: 'Gasto total',
      value: formatCurrency(metrics.totalSpend),
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      icon: Euro,
      label: 'Gasto medio',
      value: formatCurrency(metrics.averageSpend),
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
    },
    {
      icon: Calendar,
      label: 'Ultima visita',
      value: formatDate(metrics.lastVisitDate),
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      icon: Car,
      label: 'Vehiculos',
      value: metrics.vehicleCount.toString(),
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      icon: Mail,
      label: 'Emails abiertos',
      value: `${metrics.emailsOpened}/${metrics.emailsReceived}`,
      color: 'text-pink-600',
      bgColor: 'bg-pink-100',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {metricCards.map((metric) => (
          <Card key={metric.label}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${metric.bgColor}`}>
                  <metric.icon className={`h-4 w-4 ${metric.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-xs text-muted-foreground">{metric.label}</p>
                  <p className="truncate text-sm font-semibold">{metric.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Preferred Services */}
      {metrics.preferredServices.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              <h4 className="text-sm font-medium">Servicios preferidos</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {metrics.preferredServices.map((service) => (
                <span
                  key={service.serviceType}
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium"
                >
                  {service.serviceType}
                  <span className="text-muted-foreground">({service.count})</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CustomerMetricsSummarySkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div className="space-y-1">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
